import { css, jsx } from '@emotion/core';
import { Skeleton } from 'antd';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { replaceSpaceAndBr, stInnerContentStyle } from '@/src/advocacy/utils';
import Heading from '@/src/shared/components/Heading';
import CustomIcon from '@/src/shared/components/Icon';
import Sanitize from '@/src/shared/components/Sanitize';
import { bp, colors } from '@/styles';

const iconLabelNameMap = {
  'Political Climate': 'Politics',
  'Regulatory Climate': 'RegulatoryClimate',
  'Priority Legislative Issues': 'LegislativePriorities',
  'Legal Climate': 'Law',
  'Market Profile': 'Tracking',
};

function getIconByLabel(label) {
  if (!(label in iconLabelNameMap)) return null;
  return iconLabelNameMap[label];
}

const style = {
  stInnerTabs: css`
    display: flex;
    flex-wrap: wrap;
    /* justify-content: space-between; */
    flex-direction: column;
    max-height: 390px;
    align-items: center;
    @media ${bp.md} {
      justify-content: flex-start;
      flex-direction: row;
    }
    @media print {
      display: none;
    }
  `,
  tab: css`
    border-bottom: 6px solid transparent;
    max-height: 120px;
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: center;
    color: ${colors.secondaryGray};
    outline: none;
    cursor: pointer;
    padding-bottom: 10px;
    margin-bottom: 10px;
    @media (min-width: 368px) {
      &:nth-of-type(-n + 3) {
        margin-right: 30px;
      }
    }
    @media ${bp.md} {
      margin-bottom: 0;
      padding-bottom: 20px;
      margin-right: 40px;
      &:last-child {
        margin-right: 0;
      }
    }
  `,
  tabSelected: css`
    color: ${colors.primaryRed};
    border-bottom-color: ${colors.primaryRed};
  `,
  tabIcon: css`
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
  `,
  tabLabel: css`
    font-weight: bold;
    font-size: 1.2rem;
    line-height: 1.5;
    @media ${bp.lg} {
      font-size: 1.6rem;
    }
  `,
  tabCover: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    color: transparent;
  `,
  stInnerContentContainer: css`
    padding: 16px 0px;
    max-width: 867px;
    @media ${bp.md} {
      padding: 24px 32px;
    }
    @media ${bp.lg} {
      padding: 52px 32px 52px 68px;
    }
  `,
  stInnerContentHeading: css`
    margin-bottom: 8px;
    @media print {
      display: none;
    }
  `,
  stInnerContentSecondaryHeading: css`
    margin-bottom: 8px;
    margin-top: 30px;
    @media print {
      display: none;
    }
  `,
  stInnerContentPrint: css`
    display: none;
    @media print {
      display: block;
    }
  `,
  stInnerContentScreen: css`
    display: block;

    .ml-40 {
      margin-left: 4rem;
    }

    ul,
    ol {
      margin: 0 0 1.6rem;
      padding: 0 0 0 4.6rem;
    }

    @media print {
      display: none;
    }
  `,
};

class StateTabs extends PureComponent {
  state = { selectedTabIndex: 1 };

  render() {
    const { tabs, loading } = this.props;
    const { selectedTabIndex } = this.state;

    tabs.sort((x, y) => {
      if (
        x.FieldLabel === 'Legislative Update Calls' &&
        y.FieldLabel === 'Priority Legislative Issues'
      ) {
        return -1;
      }
      if (
        x.FieldLabel === 'Priority Legislative Issues' &&
        y.FieldLabel === 'Legislative Update Calls'
      ) {
        return 1;
      }
      if (x.FieldLabel === 'Legislative Update Calls') {
        return -1;
      }
      if (x.FieldLabel === 'Priority Legislative Issues') {
        return -1;
      }
      return 0;
    });

    return (
      <>
        <nav css={style.stInnerTabs}>
          {tabs.map((d, i) => {
            if (!d || !d.FieldLabel || !d.FieldValue || d.FieldLabel === 'Legislative Update Calls')
              return null;
            const divId = d.FieldLabel.toLowerCase().replace(/ /g, '-');
            return (
              <div
                tabIndex="0"
                role="button"
                key={d.FieldLabel}
                css={selectedTabIndex === i ? [style.tab, style.tabSelected] : style.tab}
                onClick={() => this.setState({ selectedTabIndex: i })}
              >
                <div css={style.tabIcon}>
                  <CustomIcon ant={false} name={getIconByLabel(d.FieldLabel)} />
                </div>
                <span css={style.tabLabel}>{d.FieldLabel}</span>
                <div
                  id={divId}
                  className={`stateNavTab stateNavTab--${i + 1}`}
                  css={style.tabCover}
                >
                  {d.FieldLabel}
                </div>
              </div>
            );
          })}
        </nav>
        <main css={style.stInnerContentContainer}>
          <Heading type={2} weight="bolder" theme="primaryGray" css={style.stInnerContentHeading}>
            {tabs[selectedTabIndex] ? (
              <>
                {selectedTabIndex === 0 || selectedTabIndex === 1 ? (
                  <>{!tabs[0].FieldValue ? tabs[1].FieldLabel : tabs[0].FieldLabel}</>
                ) : (
                  tabs[selectedTabIndex].FieldLabel
                )}
              </>
            ) : null}
          </Heading>
          {loading ? (
            <Skeleton active paragraph={{ rows: 12 }} />
          ) : (
            <>
              <Sanitize css={[stInnerContentStyle, style.stInnerContentPrint]}>
                {tabs
                  .map(tab =>
                    tab.FieldValue
                      ? `<h2 class="st-content-heading__inner">${
                          tab.FieldLabel
                        }</h2>${replaceSpaceAndBr(tab.FieldValue)}`
                      : null
                  )
                  .join('<div class="pagebreak"></div>')}
              </Sanitize>
              {selectedTabIndex === 0 || selectedTabIndex === 1 ? (
                <>
                  {!tabs[0].FieldValue ? (
                    <>
                      <Sanitize css={[stInnerContentStyle, style.stInnerContentScreen]}>
                        {replaceSpaceAndBr(tabs[1].FieldValue)}
                      </Sanitize>
                    </>
                  ) : (
                    <>
                      <Sanitize css={[stInnerContentStyle, style.stInnerContentScreen]}>
                        {replaceSpaceAndBr(tabs[0].FieldValue)}
                      </Sanitize>
                      <Heading
                        type={2}
                        weight="bolder"
                        theme="primaryGray"
                        css={style.stInnerContentSecondaryHeading}
                      >
                        {replaceSpaceAndBr(tabs[1].FieldLabel)}
                      </Heading>
                      <Sanitize css={[stInnerContentStyle, style.stInnerContentScreen]}>
                        {replaceSpaceAndBr(tabs[1].FieldValue)}
                      </Sanitize>
                    </>
                  )}
                </>
              ) : (
                <Sanitize css={[stInnerContentStyle, style.stInnerContentScreen]}>
                  {replaceSpaceAndBr(tabs[selectedTabIndex].FieldValue)}
                </Sanitize>
              )}
            </>
          )}
        </main>
      </>
    );
  }
}

StateTabs.propTypes = {
  tabs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default StateTabs;
